import React, { type FC, memo, use, useCallback, useId, useMemo, useRef, useState } from "react";
import { useInRouterContext } from "react-router-dom";
import { Button, cn } from "@heroui/react";

import { AgentDetails } from "./AgentDetails";
import { type AgentLinks, AgentRunYearContext, useAgentLinks } from "./agentLinks";
import { AgentStateIcon } from "./AgentStateIcon";
import { productRoute } from "./productRoute";
import {
    type ReportPhase,
    reportHeaderParts,
    reportLineText,
    reportPhases,
    reportToPlainText,
    stepStatusText,
    visibleBuckets,
} from "./reportText";
import type {
    AgentReportData,
    AgentReportTile,
    AgentStepId,
    AgentStepReport,
    AgentStepStatus,
    AgentTodoItem,
} from "./runner/types";
import { useAgentLabelSources } from "./useAgentEntityLabel";
import { CompanyContext } from "@domain/context/CompanyContext";
import { type MessageId, useTranslation } from "@domain/intl/useTranslation";

interface Props {
    report: AgentReportData;
    /** Runs one step again; undefined while a step runs, which hides "Jetzt nachholen" and "Erneut versuchen". */
    onRerun?(id: AgentStepId): void;
}

interface BodyProps extends Props {
    /** Null outside a router; the view then shows no links. */
    links: AgentLinks | null;
}

const linkClass = "text-xs text-accent underline underline-offset-2";

const TextLink: FC<{ onPress(): void; children: React.ReactNode }> = ({ onPress, children }) => (
    <button type="button" className={linkClass} onClick={onPress}>
        {children}
    </button>
);

const TILES: { tile: AgentReportTile; labelId: MessageId; linkId: MessageId }[] = [
    { tile: "sorted", labelId: "app.agent.report.tile.sorted", linkId: "app.agent.report.link.documents" },
    { tile: "drafts", labelId: "app.agent.report.tile.drafts", linkId: "app.agent.report.link.drafts" },
    { tile: "bankImported", labelId: "app.agent.report.tile.bankImported", linkId: "app.agent.report.link.bank" },
    { tile: "needsYou", labelId: "app.agent.report.tile.needsYou", linkId: "app.agent.report.link.list" },
];

/** The page a step's results are on, as a link label and a target; undefined when none exists today. */
const stepLink = (
    id: AgentStepId,
    links: AgentLinks,
    draftProduct: AgentReportData["draftProduct"],
): { labelId: MessageId; go(): void } | undefined => {
    switch (id) {
        case "upload":
        case "aiReading":
        case "aiRetry":
        case "autoSort":
        case "sortQuestions":
            return { labelId: "app.agent.report.link.documents", go: () => links.documents() };
        case "documentContacts":
        case "bankContacts":
            return { labelId: "app.agent.report.link.contacts", go: () => links.contacts() };
        case "kasseFromDocuments":
            return { labelId: "app.agent.report.link.kasse", go: () => links.kasse() };
        case "bankImport":
        case "fillBank":
            return { labelId: "app.agent.report.link.bank", go: () => links.bank() };
        case "draftsFromDocuments":
        case "draftsFromTransactions":
        case "aiCheck":
            return draftProduct && productRoute(draftProduct)
                ? { labelId: "app.agent.report.link.drafts", go: () => links.product(draftProduct) }
                : undefined;
        case "postingRules":
            return { labelId: "app.agent.report.link.rules", go: () => links.rules() };
        default:
            return undefined;
    }
};

/** The action of a todo as a label and a handler; undefined when it cannot be offered now. */
const todoAction = (
    item: AgentTodoItem,
    links: AgentLinks | null,
    report: AgentReportData,
    onRerun: Props["onRerun"],
): { labelId: MessageId; go(): void } | undefined => {
    const { action, stepId } = item;
    if (action === "retry" || action === "rerun") {
        return onRerun && stepId
            ? {
                  labelId: action === "retry" ? "app.agent.report.action.retry" : "app.agent.report.action.rerun",
                  go: () => onRerun(stepId),
              }
            : undefined;
    }
    if (!links) {
        return undefined;
    }
    switch (action) {
        case "openDocuments":
            return { labelId: "app.agent.report.action.resolve", go: () => links.documents() };
        case "openBank":
            return { labelId: "app.agent.report.action.resolve", go: () => links.bank() };
        case "openKasse":
            return { labelId: "app.agent.report.action.resolve", go: () => links.kasse() };
        case "openRules":
            return { labelId: "app.agent.report.action.check", go: () => links.rules() };
        case "openDrafts": {
            const product = item.product ?? report.draftProduct;
            return product && productRoute(product)
                ? { labelId: "app.agent.report.action.check", go: () => links.product(product) }
                : undefined;
        }
        default:
            return undefined;
    }
};

interface StepRowProps {
    id: AgentStepId;
    report: AgentStepReport;
    /** Set by the report step when the step can run again now. */
    action?: "retry" | "rerun";
    links: AgentLinks | null;
    draftProduct: AgentReportData["draftProduct"];
    onRerun: Props["onRerun"];
}

const StepRow: FC<StepRowProps> = ({ id, report, action, links, draftProduct, onRerun }) => {
    const { t } = useTranslation();
    const link = links && report.status === "done" ? stepLink(id, links, draftProduct) : undefined;
    const rerunId: MessageId | undefined =
        onRerun && action
            ? action === "retry"
                ? "app.agent.report.action.retry"
                : "app.agent.report.action.rerun"
            : undefined;
    const handleRerun = useCallback(() => onRerun?.(id), [onRerun, id]);
    const lines = [...report.summary, ...report.open];
    return (
        <div data-testid={`agent-report-step-${id}`} className="border-t border-separator py-1.5 first:border-t-0">
            <div className="flex items-center gap-2 text-sm">
                <AgentStateIcon state={report.status} className="size-4" />
                <span className="font-medium">{t(`app.agent.step.${id}`)}</span>
                <span className="text-muted">{stepStatusText(t, report)}</span>
                <span className="ml-auto flex gap-2">
                    {rerunId && <TextLink onPress={handleRerun}>{t(rerunId)}</TextLink>}
                    {link && <TextLink onPress={link.go}>{t(link.labelId)}</TextLink>}
                </span>
            </div>
            {lines.length > 0 && (
                <ul className="m-0 list-none pl-6 text-xs text-muted">
                    {lines.map((line, i) => (
                        <li key={`${line.textId}-${i}`}>{reportLineText(t, line)}</li>
                    ))}
                </ul>
            )}
            {report.details.length > 0 && (
                <div className="pl-6">
                    <AgentDetails items={report.details} />
                </div>
            )}
        </div>
    );
};

const CHIP_IDS: Record<AgentStepStatus, MessageId> = {
    done: "app.agent.report.phaseChip.done",
    nothing: "app.agent.report.phaseChip.nothing",
    skipped: "app.agent.report.phaseChip.skipped",
    failed: "app.agent.report.phaseChip.failed",
};

const chipTones: Record<AgentStepStatus, string> = {
    done: "bg-success/15 text-success",
    nothing: "bg-default text-muted",
    skipped: "bg-default text-muted",
    failed: "bg-danger/15 text-danger",
};

const PhaseSection: FC<
    ReportPhase & Pick<StepRowProps, "links" | "draftProduct" | "onRerun"> & { initiallyOpen: boolean }
> = ({ phase, steps, links, draftProduct, onRerun, initiallyOpen }) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(initiallyOpen);
    const contentId = useId();
    const handleToggle = useCallback(() => setExpanded((e) => !e), []);
    const counts = useMemo(() => {
        const result: Partial<Record<AgentStepStatus, number>> = {};
        for (const { report } of steps) {
            result[report.status] = (result[report.status] ?? 0) + 1;
        }
        return (Object.keys(CHIP_IDS) as AgentStepStatus[]).flatMap((status) =>
            result[status] ? [{ status, count: result[status] }] : [],
        );
    }, [steps]);
    return (
        <section className="border-t border-separator first:border-t-0">
            <button
                type="button"
                data-testid={`agent-report-phase-${phase.id}`}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm"
                aria-expanded={expanded}
                aria-controls={contentId}
                onClick={handleToggle}
            >
                <svg
                    aria-hidden
                    viewBox="0 0 12 12"
                    className={cn("size-3 motion-safe:transition-transform", expanded && "rotate-90")}
                >
                    <path d="M4 2l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.6" />
                </svg>
                <strong>{t(phase.labelId)}</strong>
                {counts.map(({ status, count }) => (
                    <span key={status} className={cn("rounded-full px-1.5 text-[11px] font-medium", chipTones[status])}>
                        {t(CHIP_IDS[status], { count })}
                    </span>
                ))}
            </button>
            {expanded && (
                <div id={contentId} className="px-3 pb-2 pl-8">
                    {steps.map(({ id, report, action }) => (
                        <StepRow
                            key={id}
                            id={id}
                            report={report}
                            action={action}
                            links={links}
                            draftProduct={draftProduct}
                            onRerun={onRerun}
                        />
                    ))}
                </div>
            )}
        </section>
    );
};

const ReportBody: FC<BodyProps> = ({ report, onRerun, links }) => {
    const { t } = useTranslation();
    const { companyGQL } = use(CompanyContext);
    const sources = useAgentLabelSources();
    const todoRef = useRef<HTMLElement>(null);

    const phases = useMemo(() => reportPhases(report), [report]);
    const buckets = useMemo(() => visibleBuckets(report), [report]);
    const draftProduct = report.draftProduct && productRoute(report.draftProduct) ? report.draftProduct : undefined;

    const handleReview = useCallback(() => {
        if (links && draftProduct) {
            links.product(draftProduct);
        }
    }, [links, draftProduct]);

    const tileLink = useCallback(
        (tile: AgentReportTile): (() => void) | undefined => {
            if (tile === "needsYou") {
                return () => todoRef.current?.scrollIntoView?.({ block: "nearest" });
            }
            if (!links) {
                return undefined;
            }
            if (tile === "sorted") {
                return () => links.documents();
            }
            if (tile === "bankImported") {
                return () => links.bank();
            }
            return draftProduct ? () => links.product(draftProduct) : undefined;
        },
        [links, draftProduct],
    );

    const handleDownload = useCallback(() => {
        const body = reportToPlainText(t, report, companyGQL?.name, sources);
        const url = URL.createObjectURL(new Blob([body], { type: "text/plain;charset=utf-8" }));
        const link = document.createElement("a");
        link.href = url;
        link.download = `binale-agent-${report.year}.txt`;
        link.click();
        URL.revokeObjectURL(url);
    }, [t, report, companyGQL?.name, sources]);

    return (
        <div className="flex flex-col gap-3">
            <p data-testid="agent-report-header" className="text-sm">
                <strong>{t("app.agent.report.finished")}</strong>
                {reportHeaderParts(t, report).map((part) => ` · ${part}`)}
            </p>

            <ul aria-label={t("app.agent.report.summary")} className="m-0 grid list-none grid-cols-2 gap-2 p-0">
                {TILES.map(({ tile, labelId, linkId }) => {
                    const count = report.tiles[tile];
                    const go = count > 0 ? tileLink(tile) : undefined;
                    return (
                        <li
                            key={tile}
                            className={cn(
                                "rounded-lg border border-separator bg-surface px-3 py-2",
                                tile === "needsYou" && count > 0 && "border-warning",
                            )}
                        >
                            <b className={cn("mr-1 text-lg", tile === "needsYou" && count > 0 && "text-warning")}>
                                {count}
                            </b>
                            <span className="text-xs text-muted">{t(labelId)}</span>
                            {go && (
                                <div>
                                    <TextLink onPress={go}>{t(linkId)}</TextLink>
                                </div>
                            )}
                        </li>
                    );
                })}
            </ul>

            <section
                ref={todoRef}
                data-testid="agent-report-todo"
                aria-label={t("app.agent.report.todoTitle")}
                className={cn(
                    "rounded-lg border px-3 py-2 text-sm",
                    report.todo.length > 0 ? "border-warning bg-warning/10" : "border-separator",
                )}
            >
                <strong>{t("app.agent.report.todoTitle")}</strong>
                <ul className="mt-1 list-disc pl-5">
                    {report.todo.length > 0 ? (
                        report.todo.map((item, i) => {
                            const action = todoAction(item, links, report, onRerun);
                            return (
                                <li key={`${item.line.textId}-${item.stepId ?? ""}-${i}`}>
                                    {reportLineText(t, item.line)}
                                    {action && (
                                        <span className="ml-1">
                                            <TextLink onPress={action.go}>{t(action.labelId)}</TextLink>
                                        </span>
                                    )}
                                </li>
                            );
                        })
                    ) : (
                        <li>{t("app.agent.report.nothingOpen")}</li>
                    )}
                </ul>
            </section>

            {buckets.length > 0 && (
                <section data-testid="agent-report-buckets" aria-label={t("app.agent.report.bucketsTitle")}>
                    <h4 className="flex items-baseline gap-2 font-semibold">
                        {t("app.agent.report.bucketsTitle")}
                        <small className="font-normal text-muted">{t("app.agent.report.bucketsLegend")}</small>
                    </h4>
                    <ul className="m-0 list-none p-0 text-sm">
                        {buckets.map((b) => (
                            <li key={b.kind} className="flex justify-between border-t border-separator py-0.5">
                                <span>{t(`app.agent.report.bucket.${b.kind}`)}</span>
                                <span className="tabular-nums">{`${b.before} → ${b.after}`}</span>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {phases.length > 0 && (
                <section aria-label={t("app.agent.report.stepsTitle")}>
                    <h4 className="mb-1 font-semibold">{t("app.agent.report.stepsTitle")}</h4>
                    <div className="rounded-lg border border-separator bg-surface">
                        {phases.map((p, index) => (
                            <PhaseSection
                                key={p.phase.id}
                                phase={p.phase}
                                steps={p.steps}
                                links={links}
                                draftProduct={draftProduct}
                                onRerun={onRerun}
                                initiallyOpen={index === 0}
                            />
                        ))}
                    </div>
                </section>
            )}

            <div className="flex flex-wrap gap-2">
                {links && draftProduct && (
                    <Button variant="primary" className="rounded-full" onPress={handleReview}>
                        {t("app.agent.report.link.draftsCount", { count: report.tiles.drafts })}
                    </Button>
                )}
                <Button variant="secondary" className="rounded-full" onPress={handleDownload}>
                    {t("app.agent.report.download")}
                </Button>
            </div>
        </div>
    );
};

const ReportWithLinks: FC<Props> = (props) => {
    const links = useAgentLinks();
    return <ReportBody {...props} links={links} />;
};

/** The final report (mockup screen f); its links minimize the run and set the report's year first. */
const AgentReportViewImpl: FC<Props> = ({ report, onRerun }) => {
    const inRouter = useInRouterContext();
    return (
        <AgentRunYearContext value={report.year}>
            {inRouter ? (
                <ReportWithLinks report={report} onRerun={onRerun} />
            ) : (
                <ReportBody report={report} onRerun={onRerun} links={null} />
            )}
        </AgentRunYearContext>
    );
};

export const AgentReportView = memo(AgentReportViewImpl);
